import { Box, Paper } from "@mui/material";
import { QueryStatus } from "@tanstack/react-query";
import { FC } from "react";
import { CellProps, Column } from "react-table";

import { CreditEvaluationLoan } from "api/creditEvaluations/types";
import Table from "components/Table";
import { dateFormatter } from "components/Table/formatters/dateFormatter";
import { percentageFormatter } from "components/Table/formatters/percentageFormatter";
import { priceFormatter } from "components/Table/formatters/priceFormatter";

interface ILoansTable {
  data: CreditEvaluationLoan[];
  status: QueryStatus;
}

const LoansTable: FC<ILoansTable> = ({ data, status }) => {
  const columns: Column<CreditEvaluationLoan>[] = [
    {
      accessor: "status",
      Header: "Status",
      Cell: ({ value }: CellProps<CreditEvaluationLoan>) => (
        <Box textTransform="capitalize">{value}</Box>
      ),
    },
    {
      accessor: "creditor",
      Header: "Creditor",
    },

    {
      accessor: "balance",
      Header: "Balance",
      Cell: ({ value }: CellProps<CreditEvaluationLoan>) => (
        <Box>{priceFormatter(value)}</Box>
      ),
    },
    {
      accessor: "payment",
      Header: "Payment",
      Cell: ({ row, value }: CellProps<CreditEvaluationLoan>) => {
        if (row.original.status === "closed" && row.original.balance > 0) {
          return <Box>-</Box>;
        }

        return <Box>{priceFormatter(Math.max(value, 0))}</Box>;
      },
    },
    {
      accessor: "hpb",
      Header: "Starting Balance/HPB",
      Cell: ({ value }: CellProps<CreditEvaluationLoan>) => (
        <Box>{priceFormatter(value)}</Box>
      ),
    },
    {
      accessor: "limit",
      Header: "Term/Limit",
    },
    {
      accessor: "opened",
      Header: "Opened",
      Cell: ({ value }: CellProps<CreditEvaluationLoan>) => (
        <Box>{dateFormatter(value)}</Box>
      ),
    },
    {
      accessor: "accountType",
      Header: "Account type",
    },
    {
      accessor: "debitToCreditRatio",
      Header: "Debt to credit ratio",
      Cell: ({ value }: CellProps<CreditEvaluationLoan>) => (
        <Box>{percentageFormatter(value * 100)}</Box>
      ),
    },
    {
      accessor: "typeDetail",
      Header: "Type Detail",
    },

    {
      accessor: "reportDate",
      Header: "Report Date",
      Cell: ({ value }: CellProps<CreditEvaluationLoan>) => (
        <Box>{dateFormatter(value)}</Box>
      ),
    },
  ];

  return (
    <Paper>
      <Table
        data={data}
        columns={columns}
        status={status}
        initialState={{
          sortBy: [
            {
              id: "reportDate",
              desc: false,
            },
          ],
        }}
        getRowProps={(row) => {
          let backgroundColor;

          if (row.original.joint) {
            backgroundColor = "#c49ec4";
          } else if (row.original.status === "closed") {
            if (row.original.balance > 0) {
              backgroundColor = "#fffbab";
            } else {
              backgroundColor = "#f5f5f5";
            }
          } else if (row.original.accountType === "Joint Account") {
            backgroundColor = "#bedfff";
          }

          return {
            sx: { backgroundColor },
          };
        }}
        additionalRows={[
          {
            cells: [
              { value: <b>Total:</b> },
              { value: null },
              {
                value: (
                  <b>
                    {priceFormatter(
                      data.reduce(
                        (prev: number, loan) => prev + loan.balance,
                        0,
                      ),
                    )}
                  </b>
                ),
              },
              {
                value: (
                  <b>
                    {priceFormatter(
                      data.reduce(
                        (prev: number, loan) =>
                          prev + Math.max(loan.payment, 0),
                        0,
                      ),
                    )}
                  </b>
                ),
              },
              { value: null },
              { value: null },
              { value: null },
              { value: null },
              { value: null },
              { value: null },
              { value: null },
              { value: null },
            ],
          },
        ]}
        manualPagination
      />
    </Paper>
  );
};

export default LoansTable;
